import { AdmRuleHistory, CreateRefreshRule, RefreshRule, UpdateRefreshRule } from '@pubstack/common/src/adm/rules'
import { sortByName } from '@pubstack/common/src/sort'
import { MutationToasts, useMutation, useQuery } from '~/api/api-client'
import { buildUrlWithSearchParams } from '~/utils/url'

export const REFRESH_RULES_QUERY_KEYS = ['adm', 'refresh-rules']
const url = '/adm/refresh-rules'
//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
/** `archived: true` asks for the set-aside rules instead of the live ones. Owner-only at the API. */
export const useRefreshRules = (withStacksUse = false, archived = false) => {
  const urlWithSearchParams = buildUrlWithSearchParams(url, { ...(withStacksUse ? { withStacksUse } : {}), ...(archived ? { archived } : {}) })
  const query = useQuery<RefreshRule[]>({
    url: urlWithSearchParams,
    // The archived listing is a different set of rows on the same endpoint, so it needs its own cache
    // entry — without `archived` in the key the two would overwrite each other.
    keys: [...REFRESH_RULES_QUERY_KEYS, { archived }],
    select: sortByName,
  })
  return { ...query, data: query.data || [] }
}

export const useRefreshRule = (id: string, enabled: boolean = !!id) => {
  return useQuery<RefreshRule>({
    url: `${url}/${id}`,
    keys: [...REFRESH_RULES_QUERY_KEYS, id],
    enabled,
  })
}

export const useRefreshRulesWithHistory = () => {
  return useQuery<AdmRuleHistory>({
    url: `${url}/history`,
    keys: [...REFRESH_RULES_QUERY_KEYS, 'history'],
  })
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateRefreshRule = (toasts: MutationToasts = {}) => {
  return useMutation<CreateRefreshRule>({
    method: 'post',
    url,
    collectionKeys: REFRESH_RULES_QUERY_KEYS,
    ...toasts,
  })
}

export const useUpdateRefreshRule = (id: string, toasts: MutationToasts = {}) => {
  return useMutation<UpdateRefreshRule, RefreshRule>({
    method: 'put',
    url: `${url}`,
    collectionKeys: [...REFRESH_RULES_QUERY_KEYS, id],
    ...toasts,
  })
}

/**
 * Set a rule aside, or bring it back. Owner-only at the API, and refused while any stack still uses it.
 *
 * `collectionKeys` covers the live list and the archived one alike — they share a prefix — so a rule
 * leaving one and appearing in the other refetches both.
 */
export const useArchiveRefreshRule = (toasts: MutationToasts = {}) => {
  return useMutation<{ id: string; archived: boolean }, RefreshRule>({
    method: 'put',
    url: ({ id }) => `${url}/${id}/archive`,
    collectionKeys: REFRESH_RULES_QUERY_KEYS,
    ...toasts,
  })
}
